import type { Prayer } from '@/types/prayer';

/**
 * Relative-time bucket assignment for the Garden vine (sticky time anchors).
 *
 * Probe-clean leaf (mirrors `remember-moments.ts`): the ONLY import is the
 * erased `import type { Prayer }`, so `scripts/probe-garden-buckets.mjs` loads
 * it under tsx with default resolution. No `@/` runtime imports, no i18n — the
 * helper returns a `kind` + an `anchorISO`; the COMPONENT maps kind -> label
 * (static i18n key, or an `Intl` weekday/month from the anchor) so the locale
 * concern stays out of this leaf (rule #7 register).
 *
 * The ladder coarsens with age so no bucket ever overwhelms and the label is
 * always human (newest -> oldest):
 *   today, yesterday, weekday (2-6 days, per-day), last week (7-13 days),
 *   earlier this month, month name (earlier month, same year), year.
 *
 * `now` is injected (never `new Date()` inside) so the probe is deterministic;
 * day math uses LOCAL calendar components fed into `Date.UTC` so day-counting
 * is DST-safe (same handling posture as `deriveMoments`).
 */

export type BucketKind =
  | 'today'
  | 'yesterday'
  | 'weekday'
  | 'lastWeek'
  | 'earlierThisMonth'
  | 'month'
  | 'year';

export interface Bucket {
  /** Stable identity for a contiguous run — a new marker is emitted only when
   *  this changes (per-day for weekday, per-month for month, per-year for year). */
  id: string;
  kind: BucketKind;
  /** The createdAt of the first prayer in the run; drives the `Intl` label for
   *  weekday/month and the numeral for year. */
  anchorISO: string;
}

export type VineItem =
  | { kind: 'bucket'; bucket: Bucket }
  | { kind: 'node'; prayer: Prayer };

function localMidnightUTC(d: Date): number {
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Whole-calendar-day difference (now - then), DST-safe. */
function dayDiff(now: Date, then: Date): number {
  return Math.round((localMidnightUTC(now) - localMidnightUTC(then)) / 86_400_000);
}

export function bucketForPrayer(createdAtISO: string, now: Date): Bucket {
  const created = new Date(createdAtISO);
  const diff = dayDiff(now, created);
  const anchorISO = createdAtISO;

  // diff <= 0 defends against a future-dated / clock-skewed prayer.
  if (diff <= 0) return { id: 'today', kind: 'today', anchorISO };
  if (diff === 1) return { id: 'yesterday', kind: 'yesterday', anchorISO };
  if (diff <= 6) {
    // Per-day marker (Monday, Sunday…) — unambiguous within the last 6 days.
    const dayKey = `${created.getFullYear()}-${created.getMonth()}-${created.getDate()}`;
    return { id: `weekday-${dayKey}`, kind: 'weekday', anchorISO };
  }
  if (diff <= 13) return { id: 'lastWeek', kind: 'lastWeek', anchorISO };

  // Older than two weeks -> absolute calendar buckets.
  const sameYear = created.getFullYear() === now.getFullYear();
  if (sameYear && created.getMonth() === now.getMonth()) {
    return { id: 'earlierThisMonth', kind: 'earlierThisMonth', anchorISO };
  }
  if (sameYear) {
    return { id: `month-${created.getFullYear()}-${created.getMonth()}`, kind: 'month', anchorISO };
  }
  return { id: `year-${created.getFullYear()}`, kind: 'year', anchorISO };
}

/**
 * Walk a DATE-DESC-sorted prayer list, emitting a bucket marker whenever the
 * bucket id changes, each followed by its node. The caller passes the already
 * date-sorted `filtered` list (the empty-query branch); during an active text
 * search the caller skips this entirely (results are relevance-ranked, not
 * date-ordered, so date buckets would be out of order).
 */
export function interleaveBucketMarkers(prayers: Prayer[], now: Date): VineItem[] {
  const out: VineItem[] = [];
  let lastId: string | null = null;
  for (const p of prayers) {
    const bucket = bucketForPrayer(p.createdAt, now);
    if (bucket.id !== lastId) {
      out.push({ kind: 'bucket', bucket });
      lastId = bucket.id;
    }
    out.push({ kind: 'node', prayer: p });
  }
  return out;
}
